/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the pharmacy management page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { RequestState, IRequestState } from '@ngt/request-utilities';

import { Button, DialogTitle, DialogContent, Dialog, DialogActions, Grid, MenuItem, FormGroup, FormControlLabel, Typography } from '@material-ui/core';

import CircularProgress from '@material-ui/core/CircularProgress';
import * as Yup from 'yup';

import { Field, FormRenderProps } from 'react-final-form';

import { useSnackbar } from 'notistack';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../../api/Neon.Dtos';
import { AsyncForm } from '../../../components/FinalForm/AsyncForm';
import { PharmacyActions } from '../../../store/modules/drugmanagement/pharmacy';
import TextFieldWrapper from '../../../components/FinalForm/FinalFormControls/TextFieldWrapper';
import { SelectWrapper } from '../../../components/FinalForm/FinalFormControls/SelectWrapper';
import NumberTextField from '../../../components/FinalForm/FinalFormControls/NumberTextField';
import { CheckboxWrapper } from '../../../components/FinalForm/FinalFormControls/CheckboxWrapper';
import useAsyncFunction from '../../../hooks/useAsyncFunction';

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IPharmacyModalProps {
    pharmacy: Dtos.Pharmacy | null;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    pharmacists: Record<number, Dtos.Personnel> | null;
    pharmacistsLoadState: IRequestState<Dtos.ResponseStatus>;
    depots: Dtos.Depot[] | null;
    depotsLoadState: IRequestState<Dtos.ResponseStatus>;
    drugs: Dtos.Drug[] | null;
    drugLoadState: IRequestState<Dtos.ResponseStatus>;
    institutions: Dtos.IInstitution[] | null;
    institutionLoadState: IRequestState<Dtos.ResponseStatus>;
    onCloseClick?: () => void;
    onFormSave?: () => void;
}

interface IPharmacyFormProps extends FormRenderProps<Dtos.Pharmacy> {
    pharmacy: Dtos.Pharmacy | null;
    pharmacists: Record<number, Dtos.Personnel> | null;
    pharmacistsLoadState: IRequestState<Dtos.ResponseStatus>;
    depots: Dtos.Depot[] | null;
    depotsLoadState: IRequestState<Dtos.ResponseStatus>;
    drugs: Dtos.Drug[] | null;
    drugLoadState: IRequestState<Dtos.ResponseStatus>;
    institutions: Dtos.IInstitution[] | null;
    institutionLoadState: IRequestState<Dtos.ResponseStatus>;
    setModalOpen: (open: boolean) => void;
    onFormSave?: () => void;
}

interface IPharmacyForm extends Partial<Dtos.Pharmacy> {
}

const formSubscriptionParams = {
    submitting: true,
    pristine: false,
    submitError: true,
    dirtySinceLastSubmit: true,
    hasValidationErrors: true,
    valid: true,
    validating: true,
    errors: true,
    form: true
};

const validation = Yup.object().shape<IPharmacyForm>({
    institutionCode: Yup.string().required().label('Institution'),
    eclipseSiteNo: Yup.string().required().label('Eclipse Site #'),
    depotId: Yup.number().required().label('Depot'),
    pharmacyStatus: Yup.number().required().label('Pharmacy Status'),
    pharmacyName: Yup.string().required().label('Pharmacy Name'),
    pharmacyDrugs: Yup.array().of(Yup.object<Dtos.PharmacyDrug>().shape({
        minThreshold: Yup.number().required().label('Minimum threshold').min(0),
        capacity: Yup.number().required().label('Capacity').min(0)
    }))
});

const PharmacyForm: React.FunctionComponent<IPharmacyFormProps> = ({
    pharmacy,
    pharmacists,
    pharmacistsLoadState,
    depots,
    depotsLoadState,
    drugs,
    institutions,
    institutionLoadState,
    setModalOpen,
    onFormSave,
    ...formRenderProps
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const onOkClick = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            const response = await formRenderProps.handleSubmit();

            if (response) {
                setModalOpen(false);

                if (onFormSave) {
                    onFormSave();
                }
            }
        }
        catch (error: any) {
            enqueueSnackbar('An error occurred while attempting to save the pharmacy.', { variant: 'error' });
        }
    }, [setModalOpen, enqueueSnackbar, formRenderProps, onFormSave]);

    const onCloseClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setModalOpen(false);
    }, [setModalOpen]);

    const institutionItems = React.useMemo(() => {
        if (!institutions || institutions.length === 0) {
            return [];
        }
        else {
            return institutions.map(institution => {
                return <MenuItem
                    key={institution.id}
                    value={institution.code}
                >
                    {institution.name}
                </MenuItem>;
            });
        }
    }, [institutions]);

    const depotItems = React.useMemo(() => {
        if (!depots || depots.length === 0) {
            return [];
        }
        else {
            return depots.map(depot => {
                return <MenuItem
                    key={depot.id}
                    value={depot.id}
                    disabled={!depot.active}
                >
                    {depot.depotName}
                </MenuItem>;
            });
        }
    }, [depots]);

    const pharmacistItems = React.useMemo(() => {
        if (!pharmacists || Object.keys(pharmacists).length === 0) {
            return [];
        }
        else {
            return Object.keys(pharmacists).map(key => {
                return <MenuItem
                    key={key}
                    value={key}
                >
                    {pharmacists[key]?.name}
                </MenuItem>;
            });
        }
    }, [pharmacists]);

    const formDisabled = React.useMemo(() => {
        return formRenderProps.submitting;
    }, [formRenderProps.submitting]);

    const pharmacyDrugs = React.useMemo(() => {
        if (pharmacy &&
            pharmacy.pharmacyDrugs &&
            pharmacy.pharmacyDrugs.length > 0) {
            return pharmacy.pharmacyDrugs.map((pd, index) => {
                return (
                    <React.Fragment key={index}>
                        <Grid item xs={12}>
                            <Typography
                                variant="h4"
                                color="secondary"
                            >
                                {drugs?.find(d => d.id === pd.drugId)?.drugName}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Field
                                fullWidth
                                name={'pharmacyDrugs[' + index + '].minThreshold'}
                                component={NumberTextField}
                                type="text"
                                label="Minimum Threshold"
                                disabled={true}
                                defaultValue={pd.minThreshold}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Field
                                fullWidth
                                name={'pharmacyDrugs[' + index + '].capacity'}
                                component={NumberTextField}
                                type="text"
                                label="Capacity"
                                disabled={formDisabled}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Field
                                            name={'pharmacyDrugs[' + index + '].enabled'}
                                            component={CheckboxWrapper}
                                            label="Enabled"
                                            defaultValue={true}
                                            disabled={formDisabled}
                                        />
                                    }
                                    label="Enabled"
                                />
                            </FormGroup>
                        </Grid>
                    </React.Fragment>)
            });
        }

        return null;
    }, [pharmacy, drugs, formDisabled]);

    return (
        <>
            <DialogContent>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12}>
                        {
                            institutionLoadState && institutionLoadState.state && institutionLoadState.state === RequestState.Pending ?
                                <CircularProgress /> :
                                <Field
                                    name="institutionCode"
                                    component={SelectWrapper}
                                    type="text"
                                    disabled={true}
                                    label="Institution"
                                    formControlProps={{
                                        fullWidth: true
                                    }}
                                >
                                    {institutionItems}
                                </Field>
                        }
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="eclipseSiteNo"
                            component={TextFieldWrapper}
                            type="text"
                            label="Eclipse Site #"
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            name="pharmacyStatus"
                            component={SelectWrapper}
                            type="text"
                            disabled={formDisabled}
                            label="Status"
                            formControlProps={{
                                fullWidth: true
                            }}
                        >
                            <MenuItem value={Dtos.PharmacyStatus.Inactive}>Inactive</MenuItem>
                            <MenuItem value={Dtos.PharmacyStatus.Open}>Open</MenuItem>
                            <MenuItem value={Dtos.PharmacyStatus.Closed}>Closed</MenuItem>
                        </Field>
                    </Grid>
                    <Grid item xs={12}>
                        {
                            depotsLoadState && depotsLoadState.state && depotsLoadState.state === RequestState.Pending ?
                                <CircularProgress /> :
                                <Field
                                    name="depotId"
                                    component={SelectWrapper}
                                    type="text"
                                    disabled={formDisabled}
                                    label="Depot"
                                    formControlProps={{
                                        fullWidth: true
                                    }}
                                >
                                    {depotItems}
                                </Field>
                        }
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="pharmacyName"
                            component={TextFieldWrapper}
                            type="text"
                            label="Pharmacy Name"
                            disabled={formDisabled}
                        />
                    </Grid>
                    {
                        pharmacyDrugs
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseClick} color="primary" disabled={formDisabled}>
                    Cancel
                </Button>
                <Button onClick={onOkClick}
                    color="primary" autoFocus disabled={formDisabled}>
                    OK
                </Button>
            </DialogActions>
        </>
    );
};

const PharmacyModal: React.FunctionComponent<IPharmacyModalProps> = ({
    pharmacy,
    modalOpen,
    setModalOpen,
    pharmacists,
    pharmacistsLoadState,
    depots,
    depotsLoadState,
    drugs,
    drugLoadState,
    institutions,
    institutionLoadState,
    onCloseClick,
    onFormSave
}) => {

    const pharmacyForm = React.useCallback((props: FormRenderProps<Dtos.Pharmacy>) => {
        return (
            <PharmacyForm
                pharmacy={pharmacy}
                pharmacists={pharmacists}
                pharmacistsLoadState={pharmacistsLoadState}
                depots={depots}
                depotsLoadState={depotsLoadState}
                drugs={drugs}
                drugLoadState={drugLoadState}
                institutions={institutions}
                institutionLoadState={institutionLoadState}
                setModalOpen={setModalOpen}
                onFormSave={onFormSave}
                {...props}
            />
        );
    }, [institutions, institutionLoadState, setModalOpen, pharmacy, depots, depotsLoadState, drugLoadState, drugs, onFormSave, pharmacists, pharmacistsLoadState]);

    const asyncSave = useAsyncFunction(PharmacyActions.save, PharmacyActions.saveSuccess, PharmacyActions.saveFailure);

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onCloseClick}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Save Pharmacy
                </DialogTitle>
                <AsyncForm
                    initialValues={pharmacy}
                    validate={validation}
                    onSubmit={asyncSave}
                    formSubscription={formSubscriptionParams}
                    validationDebounceTimeout={200}
                    component={pharmacyForm}
                />
            </Dialog>
        </>
    );
}

export default PharmacyModal;